import React, { useEffect, useState } from "react";
import { X, CreditCard, Wallet, Landmark, Banknote, ReceiptText, ArrowRight, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const ICONS = { "credit-card": CreditCard, paypal: Wallet, klarna: Banknote, bank: Landmark, landmark: Landmark, receipt: ReceiptText };

export default function CheckoutDialog({ invoice, checkoutBody, summary, onClose, onPaid }) {
  const { lang } = useI18n();
  const de = lang === "de";
  const [methods, setMethods] = useState(null);
  const [selected, setSelected] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bank, setBank] = useState(null);

  // Reference sent to the payment API + display summary (supports invoices OR shop orders)
  const ref = checkoutBody || (invoice ? { invoice_id: invoice.id } : {});
  const disp = summary || (invoice
    ? { label: invoice.number, total: invoice.total, currency: invoice.currency }
    : { label: "", total: 0, currency: "eur" });

  useEffect(() => {
    api.get("/payments/methods", { params: { amount: disp.total, currency: (disp.currency || "eur").toLowerCase() } })
      .then((r) => { setMethods(r.data); if (r.data[0]) setSelected(r.data[0].key); })
      .catch((e) => { toast.error(apiErr(e)); setMethods([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disp.total]);

  const chosen = (methods || []).find((m) => m.key === selected);
  const needsConsent = chosen?.key === "sepa_debit";

  const proceed = async () => {
    if (needsConsent && !consent) { toast.error(de ? "Bitte SEPA-Mandat bestätigen" : "Please accept the SEPA mandate"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { ...ref, method: selected, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      if (data.method === "bank_transfer") { setBank({ ...data.bank, reference: data.reference }); setLoading(false); return; }
    } catch (e) { toast.error(apiErr(e)); setLoading(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t || ""); toast.success(de ? "Kopiert" : "Copied"); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="checkout-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative surface w-full max-w-lg max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="font-display font-bold text-lg">{de ? "Zahlung" : "Payment"}</h2>
            {(!invoice || !(invoice.vat || invoice.tax)) && <p className="text-xs text-muted-foreground">{de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."}</p>}
            <p className="text-sm text-muted-foreground">{disp.label ? `${disp.label} · ` : ""}{(disp.total || 0).toFixed(2)} {(disp.currency || "EUR").toUpperCase()}</p>
          </div>
          <button onClick={onClose} data-testid="checkout-close"><X size={18} /></button>
        </div>

        {bank ? (
          <div className="p-5 space-y-4" data-testid="checkout-bank-info">
            <p className="text-sm">{de ? bank.instructions_de : bank.instructions_en}</p>
            <div className="surface p-4 space-y-2 text-sm">
              {[[de ? "Kontoinhaber" : "Account holder", bank.bank_holder], [de ? "Bank" : "Bank", bank.bank_name],
                ["IBAN", bank.bank_iban], ["BIC", bank.bank_bic], [de ? "Verwendungszweck" : "Reference", bank.reference]].map(([l, v]) => (
                <div key={l} className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">{l}</span>
                  <span className="font-mono flex items-center gap-2">{v || "—"} {v && <button onClick={() => copy(v)} className="hover:text-primary"><Copy size={13} /></button>}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{de ? "Ihre Bestellung wird freigeschaltet, sobald wir den Zahlungseingang bestätigen." : "Your order will be unlocked once we confirm receipt of payment."}</p>
            <button onClick={() => { onPaid && onPaid(); onClose(); }} className="btn-primary w-full" data-testid="checkout-bank-done">{de ? "Verstanden" : "Got it"}</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {methods === null && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-primary" /></div>}
            {methods && methods.length === 0 && <p className="text-muted-foreground text-center py-6">{de ? "Keine Zahlungsart verfügbar." : "No payment method available."}</p>}
            {(methods || []).map((m) => {
              const Icon = ICONS[m.icon] || CreditCard;
              const active = selected === m.key;
              return (
                <button key={m.key} onClick={() => setSelected(m.key)} data-testid={`checkout-method-${m.key}`}
                  className={`w-full text-left flex items-start gap-3 rounded-lg border p-4 transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                  <Icon size={22} className={active ? "text-primary" : "text-muted-foreground"} />
                  <div className="min-w-0">
                    <div className="font-semibold">{de ? m.name_de : m.name_en}</div>
                    <div className="text-xs text-muted-foreground">{de ? m.desc_de : m.desc_en}</div>
                  </div>
                  {active && <Check size={18} className="text-primary ml-auto shrink-0" />}
                </button>
              );
            })}

            {needsConsent && (
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground surface p-3" data-testid="checkout-sepa-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="h-4 w-4 mt-0.5 accent-[hsl(21,100%,50%)]" />
                <span>{de ? chosen.mandate_text_de : chosen.mandate_text_en}</span>
              </label>
            )}

            {methods && methods.length > 0 && (
              <button onClick={proceed} disabled={loading || !selected} className="btn-primary w-full" data-testid="checkout-proceed">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <>{de ? "Weiter zur Zahlung" : "Continue to payment"} <ArrowRight size={16} /></>}
              </button>
            )}
            <p className="text-[11px] text-center text-muted-foreground">{de ? "Sichere Zahlung. Karten & Klarna/SEPA über Stripe, PayPal über PayPal." : "Secure payment. Cards & Klarna/SEPA via Stripe, PayPal via PayPal."}</p>
          </div>
        )}
      </div>
    </div>
  );
}
